/**
 * RadarChart Component
 *
 * SVG hexagonal spider chart showing CAMEL component scores.
 * Shows current values vs optimal (max) values.
 *
 * Shared component used by both InstitutionDashboard and Analyst Bench.
 */

import { useTranslation } from 'react-i18next';
import type { CAMELComponent, CAMELCode } from '../../../constants/camelConstants';
import { camelLabels, radarOrderedCodes } from '../../../constants/camelConstants';
import styles from './RadarChart.module.css';

interface RadarChartProps {
  /** Current CAMEL component scores */
  camelData: CAMELComponent[];
}

export default function RadarChart({ camelData }: RadarChartProps) {
  const { t } = useTranslation();
  const chartRadius = 85;
  const labelPaddingX = 80; // More horizontal space for side labels
  const labelPaddingY = 50; // Vertical space for top/bottom labels
  const width = (chartRadius + labelPaddingX) * 2;
  const height = (chartRadius + labelPaddingY) * 2;
  const centerX = width / 2;
  const centerY = height / 2;
  const maxRadius = chartRadius;
  const levels = 5;

  // Get point coordinates on the hexagon
  const getPointOnCircle = (index: number, value: number, maxValue: number) => {
    const angle = (Math.PI * 2 * index) / radarOrderedCodes.length - Math.PI / 2;
    const normalizedValue = (value / maxValue) * maxRadius;
    const x = centerX + normalizedValue * Math.cos(angle);
    const y = centerY + normalizedValue * Math.sin(angle);
    return { x, y };
  };

  // Get label position (outside the chart)
  const getLabelPosition = (index: number) => {
    const angle = (Math.PI * 2 * index) / radarOrderedCodes.length - Math.PI / 2;
    const labelRadius = maxRadius + 28;
    const x = centerX + labelRadius * Math.cos(angle);
    const y = centerY + labelRadius * Math.sin(angle);
    return { x, y };
  };

  // Calculate actual data points
  const actualPoints = radarOrderedCodes.map((code, i) => {
    const item = camelData.find((c) => c.code === code);
    const maxVal = camelLabels[code].max;
    return getPointOnCircle(i, item?.score || 0, maxVal);
  });

  // Calculate optimal (max) points
  const optimalPoints = radarOrderedCodes.map((code, i) => {
    const maxVal = camelLabels[code].max;
    return getPointOnCircle(i, maxVal, maxVal);
  });

  // Convert points to SVG path
  const pointsToPath = (points: { x: number; y: number }[]) => {
    return points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ') + ' Z';
  };

  // Generate grid lines (hexagonal)
  const gridPaths = Array.from({ length: levels }, (_, i) => {
    const radius = (maxRadius / levels) * (i + 1);
    const points = radarOrderedCodes.map((_, idx) => {
      const angle = (Math.PI * 2 * idx) / radarOrderedCodes.length - Math.PI / 2;
      return {
        x: centerX + radius * Math.cos(angle),
        y: centerY + radius * Math.sin(angle),
      };
    });
    return pointsToPath(points);
  });

  // Generate axis lines from center to each vertex
  const axisLines = radarOrderedCodes.map((_, i) => {
    const angle = (Math.PI * 2 * i) / radarOrderedCodes.length - Math.PI / 2;
    return {
      x2: centerX + maxRadius * Math.cos(angle),
      y2: centerY + maxRadius * Math.sin(angle),
    };
  });

  return (
    <div className={styles.radarContainer}>
      <div className={styles.radarHeader}>
        <span className={styles.radarTitle}>{t('camel.performanceOverview')}</span>
      </div>

      <div className={styles.radarChartWrapper}>
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
          {/* Grid circles (hexagonal) */}
          {gridPaths.map((path, i) => (
            <path
              key={i}
              d={path}
              fill="none"
              stroke="var(--chart-grid)"
              strokeWidth="1"
              strokeDasharray={i === levels - 1 ? '4,4' : 'none'}
            />
          ))}

          {/* Axis lines */}
          {axisLines.map((line, i) => (
            <line
              key={i}
              x1={centerX}
              y1={centerY}
              x2={line.x2}
              y2={line.y2}
              stroke="var(--chart-grid)"
              strokeWidth="1"
            />
          ))}

          {/* Optimal outline (dashed) */}
          <path
            d={pointsToPath(optimalPoints)}
            fill="none"
            stroke="rgba(255,255,255,0.2)"
            strokeWidth="1.5"
            strokeDasharray="6,4"
          />

          {/* Actual data polygon */}
          <path
            d={pointsToPath(actualPoints)}
            fill="var(--chart-area-fill)"
            stroke="var(--chart-line-primary)"
            strokeWidth="2"
            style={{ transition: 'all 0.4s ease' }}
          />

          {/* Data points */}
          {actualPoints.map((point, i) => (
            <circle
              key={i}
              cx={point.x}
              cy={point.y}
              r="4"
              fill="var(--chart-line-primary)"
              stroke="#ffffff"
              strokeWidth="2"
              style={{ transition: 'all 0.4s ease' }}
            />
          ))}

          {/* Labels */}
          {radarOrderedCodes.map((code, i) => {
            const pos = getLabelPosition(i);
            const label = camelLabels[code as CAMELCode].shortName;

            let textAnchor: 'middle' | 'start' | 'end' = 'middle';
            let dy = 0;
            if (pos.x < centerX - 20) textAnchor = 'end';
            else if (pos.x > centerX + 20) textAnchor = 'start';
            if (pos.y < centerY - 50) dy = -5;
            else if (pos.y > centerY + 50) dy = 12;

            return (
              <text
                key={code}
                x={pos.x}
                y={pos.y}
                textAnchor={textAnchor}
                dy={dy}
                fill="var(--dashboard-text-secondary)"
                fontSize="11"
                fontWeight="500"
              >
                {label}
              </text>
            );
          })}
        </svg>
      </div>

      <div className={styles.radarLegend}>
        <div className={styles.legendItem}>
          <span className={`${styles.legendLine} ${styles.actual}`}></span>
          <span>{t('radar.actual')}</span>
        </div>
        <div className={styles.legendItem}>
          <span className={`${styles.legendLine} ${styles.optimal}`}></span>
          <span>{t('radar.optimal')}</span>
        </div>
      </div>
    </div>
  );
}
